import React, { useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import './MediaCalendar.css';
import { useConsoleTheme } from '../../../lib/theme';

// F1 — shared month/week/day calendar for the media surface. Role pages pass
// their own events + handlers; this component owns the localizer, dark theme,
// status colouring, and the drag-and-drop wrapper.

// No explicit locale map — date-fns defaults to en-US formatting, which is all
// we need. (Importing date-fns/locale pulls an ESM-only submodule that jest's
// resolver can't load; the empty map sidesteps it without affecting output.)
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales: {},
});

const DnDCalendar = withDragAndDrop(Calendar);

// media_event status → accent colour.
const STATUS_COLORS = {
  pending: '#f59e0b',   // amber — awaiting confirm/approval
  booked: '#B0FC38',    // lime — confirmed
  shooting: '#3b82f6',  // blue
  editing: '#3b82f6',
  delivered: '#B0FC38', // green
  cancelled: '#6b7280', // gray
  rescheduled_out: '#6b7280',
};

function eventStyleGetter(event) {
  const base = STATUS_COLORS[event.status] || '#B0FC38';
  const dim = event.status === 'cancelled' || event.status === 'rescheduled_out';
  return {
    style: {
      backgroundColor: dim ? 'rgba(255,255,255,0.03)' : `${base}33`,
      borderLeft: `3px solid ${base}`,
      color: '#f3f4f6',
      borderRadius: '6px',
      opacity: dim ? 0.55 : 1,
      textDecoration: dim ? 'line-through' : 'none',
      fontSize: '11px',
      boxShadow: dim ? 'none' : `0 1px 2px rgba(0,0,0,0.25)`,
    },
  };
}

// Light mode (docs/light-mode.md §3): the same status hues, one step darker so
// the chip edge and the legend dot reach 3:1 on paper, a pale tint of the hue
// behind ink text. Dark mode keeps eventStyleGetter exactly as it was.
const STATUS_COLORS_LIGHT = {
  pending: '#b45309',   // amber-700
  booked: '#4D7C0F',    // accent
  shooting: '#2563eb',  // blue-600
  editing: '#2563eb',
  delivered: '#4D7C0F',
  cancelled: '#5C584C', // ink-3
  rescheduled_out: '#5C584C',
};
const STATUS_TINTS_LIGHT = {
  '#b45309': 'rgba(217, 119, 6, 0.16)',
  '#4D7C0F': 'rgba(77, 124, 15, 0.16)',
  '#2563eb': 'rgba(37, 99, 235, 0.13)',
};

function eventStyleGetterLight(event) {
  const base = STATUS_COLORS_LIGHT[event.status] || STATUS_COLORS_LIGHT.booked;
  const dim = event.status === 'cancelled' || event.status === 'rescheduled_out';
  return {
    style: {
      backgroundColor: dim ? 'rgba(28, 27, 22, 0.04)' : STATUS_TINTS_LIGHT[base],
      borderLeft: `3px solid ${base}`,
      color: '#1C1B16',
      borderRadius: '6px',
      opacity: dim ? 0.7 : 1,
      textDecoration: dim ? 'line-through' : 'none',
      fontSize: '11px',
      boxShadow: dim ? 'none' : '0 1px 2px rgba(60, 50, 30, 0.12)',
    },
  };
}

// Legend strip under the grid so the status colours read as meaning, not decoration.
const LEGEND = [
  { status: 'pending', label: 'Awaiting' },
  { status: 'booked', label: 'Booked' },
  { status: 'shooting', label: 'In production' },
  { status: 'delivered', label: 'Delivered' },
  { status: 'cancelled', label: 'Cancelled' },
];

export default function MediaCalendar({
  events = [],
  onSelectEvent,
  onSelectSlot,
  onEventDrop,
  selectable = false,
  draggable = false,
  defaultView = 'month',
  views = ['month', 'week', 'day'],
  height = 640,
  min,
  max,
}) {
  const { isLight } = useConsoleTheme();
  const legendColors = isLight ? STATUS_COLORS_LIGHT : STATUS_COLORS;

  // Normalize incoming rows → react-big-calendar shape (start/end as Date).
  const calEvents = useMemo(() => (events || [])
    .map((e) => {
      const start = e.start instanceof Date ? e.start : new Date(e.start);
      const end = e.end instanceof Date ? e.end : new Date(e.end || e.start);
      if (Number.isNaN(start.valueOf())) return null;
      return {
        ...e,
        title: e.title || e.eventTitle || 'Booking',
        start,
        end: Number.isNaN(end.valueOf()) ? start : end,
        allDay: false,
      };
    })
    .filter(Boolean), [events]);

  const CalComponent = draggable ? DnDCalendar : Calendar;
  const dndProps = draggable
    ? { onEventDrop, resizable: false, draggableAccessor: (e) => e.status !== 'cancelled' && e.status !== 'delivered' }
    : {};

  return (
    <div className="nexsav-media-calendar-shell">
      <div className="nexsav-media-calendar" style={{ height }}>
        <CalComponent
          localizer={localizer}
          events={calEvents}
          startAccessor="start"
          endAccessor="end"
          defaultView={defaultView}
          views={views}
          popup
          selectable={selectable}
          onSelectEvent={onSelectEvent}
          onSelectSlot={onSelectSlot}
          eventPropGetter={isLight ? eventStyleGetterLight : eventStyleGetter}
          min={min}
          max={max}
          {...dndProps}
        />
      </div>
      <div className="nexsav-media-calendar-legend">
        {LEGEND.map((l) => (
          <span key={l.label} className="nexsav-media-calendar-legend__item">
            <span className="nexsav-media-calendar-legend__dot" style={{ backgroundColor: legendColors[l.status] }} />
            {l.label}
          </span>
        ))}
      </div>
    </div>
  );
}
